import { useState, useEffect, useRef } from 'react'
import { Select, MenuItem, ListSubheader, TextField, CircularProgress, useTheme } from '@mui/material'


// ─── Mode colors ─────────────────────────────────────────────────────────────

const MODE_COLORS = {
  imageFit: { main: '#5C6BC0', bg: 'rgba(92,107,192,0.12)', bgHover: 'rgba(92,107,192,0.3)', light: 'rgba(92,107,192,0.06)' },
  spaceFill: { main: '#26A69A', bg: 'rgba(38,166,154,0.12)', bgHover: 'rgba(38,166,154,0.3)', light: 'rgba(38,166,154,0.06)' },
}

// ─── UndoButton ──────────────────────────────────────────────────────────────

const UNDO_DURATION = 5000

function UndoButton({ undoId, onUndo, size = 'normal' }) {
  const theme = useTheme()
  const neutralDark = theme.palette.neutral?.dark || '#686868'
  const [progress, setProgress] = useState(100)
  const startRef = useRef(Date.now())

  useEffect(() => {
    startRef.current = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startRef.current
      const remaining = Math.max(0, 100 - (elapsed / UNDO_DURATION) * 100)
      setProgress(remaining)
      if (remaining <= 0) clearInterval(interval)
    }, 50)
    return () => clearInterval(interval)
  }, [undoId])

  const isSmall = size === 'small'
  const circleSize = isSmall ? 16 : 20

  return (
    <span
      onClick={e => { e.stopPropagation(); onUndo?.(undoId) }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: isSmall ? '2px 8px' : '3px 10px',
        borderRadius: 4,
        background: neutralDark, color: '#fff',
        fontSize: isSmall ? 10 : 11, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        cursor: 'pointer', userSelect: 'none',
      }}
    >
      <span style={{ position: 'relative', width: circleSize, height: circleSize, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress
          variant="determinate"
          value={progress}
          size={circleSize}
          thickness={5}
          sx={{ color: 'rgba(255,255,255,0.8)', position: 'absolute' }}
        />
      </span>
      Undo
    </span>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getGroupId(layoutId, config) {
  const layout = config?.layouts?.find(l => l.id === layoutId)
  if (!layout) return null
  if (layout.id.endsWith('img')) return 'img'
  if (layout.type === 'table') return 'table'
  if (layout.id.startsWith('bkg')) return 'bkg'
  return 'std'
}

// Ordine taglia desiderato per i layout immagine (e standard con naming analogo)
const SIZE_ORDER = ['S', 'S1', 'S2', 'M', 'M1', 'L', 'L1', 'XL', 'XXL']

function sizeRank(id) {
  // Rimuove suffisso "img" o prefisso "bkg" per estrarre la taglia
  const key = id.replace(/img$/i, '').replace(/^bkg/i, '').toUpperCase()
  const i = SIZE_ORDER.indexOf(key)
  return i === -1 ? 999 : i
}

function buildLayoutGroups(config, hasBkg, currentGroupId) {
  const layouts = config?.layouts ?? []
  const allGroups = [
    { label: 'Image only', id: 'img', items: layouts.filter(l => l.id.endsWith('img')).sort((a, b) => sizeRank(a.id) - sizeRank(b.id)) },
    { label: 'Standard', id: 'std', items: layouts.filter(l => !l.id.endsWith('img') && l.type !== 'table' && !l.id.startsWith('bkg')).sort((a, b) => sizeRank(a.id) - sizeRank(b.id)) },
    { label: 'Tabella', id: 'table', items: layouts.filter(l => l.type === 'table') },
    { label: 'Background', id: 'bkg', items: layouts.filter(l => l.id.startsWith('bkg')) },
  ]
  return allGroups
    .filter(g => !currentGroupId || g.id === currentGroupId)
    .filter(g => g.id !== 'bkg' || hasBkg)
    .filter(g => g.items.length > 0)
}

const brToNl = v => String(v || '').replace(/<br\s*\/?>/gi, '\n')
const nlToBr = v => v.replace(/\n/g, '<br>')

const quietFieldSx = {
  '& .MuiInput-underline:before': { borderBottom: 'none' },
  '& .MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '1px solid #d0d0d0 !important' },
  '& .MuiInput-underline:after': { borderBottom: '2px solid #9e9e9e' },
}

// ─── LayoutIcon ──────────────────────────────────────────────────────────────

/**
 * Mini-preview SVG del layout nella griglia 6×12.
 * Mostra l'ingombro (cols×rows) come rettangolo pieno.
 * Per i layout tabella (rows variabile), mostra il tier minimo con bordo tratteggiato.
 */
function LayoutIcon({ layout, nativeGrids }) {
  const GRID_COLS = 6, GRID_ROWS = 12
  const W = 30, H = 36  // 5px/col, 3px/row
  const cellW = W / GRID_COLS
  const cellH = H / GRID_ROWS

  let cols = layout.cols || GRID_COLS
  let rows = layout.rows || GRID_ROWS
  let isVariable = false

  // Table: usa il tier più piccolo, indica variabilità
  if (layout.type === 'table' && layout.variants?.[0]?.tiers?.length) {
    const tiers = layout.variants[0].tiers
    cols = tiers[0].cols
    rows = tiers[0].rows
    isVariable = true
  }

  const fw = cols * cellW
  const fh = rows * cellH

  // Griglia di riferimento: letta da config (nativeGrids + layout.nativeGrid), fallback 3×3
  const gridDef = nativeGrids?.[layout.nativeGrid]
  const refCols = gridDef?.cols ?? 3
  const refRows = gridDef?.rows ?? 3
  const refLines = []
  for (let c = 1; c < refCols; c++) {
    refLines.push({ x1: W * c / refCols, y1: 0, x2: W * c / refCols, y2: H })
  }
  for (let r = 1; r < refRows; r++) {
    refLines.push({ x1: 0, y1: H * r / refRows, x2: W, y2: H * r / refRows })
  }

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ flexShrink: 0 }}>
      {/* Sfondo griglia */}
      <rect x={0} y={0} width={W} height={H} fill="#f8fafc" stroke="#e2e8f0" strokeWidth={0.5} rx={1} />
      {/* Linee di riferimento pagina */}
      {refLines.map((l, i) => (
        <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke="#cbd5e1" strokeWidth={0.5} strokeDasharray="1.5,1.5" />
      ))}
      {/* Footprint layout */}
      <rect
        x={0.5} y={0.5}
        width={fw - 1} height={fh - 1}
        fill={isVariable ? '#fef3c7' : '#dbeafe'}
        stroke={isVariable ? '#f59e0b' : '#60a5fa'}
        strokeWidth={0.7}
        strokeDasharray={isVariable ? '2,1' : 'none'}
        rx={0.5}
      />
      {/* Freccia ↕ per tier variabile */}
      {isVariable && (
        <text x={fw / 2} y={fh / 2 + 1} textAnchor="middle" fontSize={7} fill="#b45309">↕</text>
      )}
    </svg>
  )
}

// ─── Advisor column constants ────────────────────────────────────────────────

const ADVISOR_COL_W = 90

// ─── AdvisorCell ─────────────────────────────────────────────────────────────

function AdvisorCell({ advisor, suggestion, undoEntry, onAccept, onReject, onUndo }) {
  const colors = MODE_COLORS[advisor]

  return (
    <span style={{
      minWidth: ADVISOR_COL_W, width: ADVISOR_COL_W, flexShrink: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
      padding: '2px 4px',
    }}>
      {undoEntry ? (
        <UndoButton undoId={undoEntry.id} onUndo={onUndo} size="small" />
      ) : suggestion?.status === 'pending' ? (
        <>
          <span style={{ fontSize: 11, color: colors.main, whiteSpace: 'nowrap', fontWeight: 600, textAlign: 'center' }}>
            {suggestion.original} → {suggestion.suggested}
          </span>
          <span style={{ display: 'flex', gap: 3 }}>
            <span onClick={e => { e.stopPropagation(); onAccept?.() }}
              style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: 'pointer', background: colors.main, color: '#fff' }}>✓</span>
            <span onClick={e => { e.stopPropagation(); onReject?.() }}
              style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: 'pointer', background: 'rgba(0,0,0,0.08)', color: '#777' }}>✗</span>
          </span>
        </>
      ) : (
        <span style={{ color: '#d0d0d0', fontSize: 12 }}>—</span>
      )}
    </span>
  )
}

// ─── PlacementRow ─────────────────────────────────────────────────────────────

function PlacementRow({ placement, index, config, onChange, onChangeProduct, onChangeTableRow, onDragStart, onDragOver, onDrop, onDragEnd, isDragOver, onHover, isSelected, onRowClick, hasOverflow, overflowSet, activeAdvisors, imageFitSuggestion, imageFitSuggestions, spaceFillSuggestion, onAcceptSuggestion, onRejectSuggestion, undoEntries, onUndo, showSpaceFill, showImageFit, allowedLayouts = null }) {
  const { layoutId, imgSize, product, tableRows, products, chunkIndex = 0, totalChunks = 1 } = placement
  const csvMapping = config?.meta?.csvMapping ?? {}
  const F = {
    articleName:        csvMapping['articleName']        ?? 'Article Name',
    articleDescription: csvMapping['articleDescription'] ?? 'Article Text',
    articleLabel:       csvMapping['articleLabel']       ?? 'Article Number Text',
    articleNumber:      csvMapping['articleNumber']      ?? 'Article Number',
    image:              csvMapping['image']              ?? 'Picture Name',
    imgSize:            csvMapping['imgSize']            ?? 'Image Size',
    backgroundImage:    csvMapping['background image']   ?? 'background image',
  }
  const hasBkg = !!(product?.[F.backgroundImage])
  const currentGroup = getGroupId(layoutId, config)
  const groups = buildLayoutGroups(config, hasBkg, currentGroup)
  const isTable = currentGroup === 'table'
  const isBkg = currentGroup === 'bkg'
  const isBkg2 = layoutId === 'bkg2'
  const subRows = tableRows ?? products ?? []
  const artNum = product?.[F.articleNumber] || ''
  const artName = product?.[F.articleName] || ''
  const artText = product?.[F.articleDescription] || ''

  // Collasso sub-righe (solo per blocchi tabella)
  const [subRowsCollapsed, setSubRowsCollapsed] = useState(false)

  const selectedStyle = isSelected
    ? { background: '#f5f5f5', borderLeft: '3px solid #9e9e9e' }
    : {}

  // dragProps per la riga: gestisce solo il drop target, NON il drag source
  // (draggable e onDragStart sono sul drag handle per non interferire con la selezione testo)
  const dragProps = {
    onDragOver: e => { e.preventDefault(); onDragOver(index) },
    onDrop: () => onDrop(index),
    onDragEnd: () => onDragEnd(),
    onMouseEnter: () => onHover(index),
    onMouseLeave: () => onHover(null),
    onClick: () => onRowClick?.(index),
  }

  // handleProps: applicati solo sul drag handle (⠿)
  const handleProps = {
    draggable: true,
    onDragStart: () => onDragStart(index),
  }

  const filteredGroups = allowedLayouts
    ? groups.map(g => ({ ...g, items: g.items.filter(l => allowedLayouts.includes(l.id)) })).filter(g => g.items.length > 0)
    : groups

  const layoutSelect = (
    <Select
      value={layoutId}
      onChange={e => onChange(index, 'layoutId', e.target.value)}
      renderValue={v => v}
      size="small"
      disabled={isBkg}
      sx={{ fontSize: 11, minWidth: 90, flexShrink: 0 }}
    >
      {filteredGroups.flatMap(group => [
        <ListSubheader key={`hdr-${group.label}`} sx={{ fontSize: 10, lineHeight: '24px', minHeight: 24 }}>
          {group.label}
        </ListSubheader>,
        ...group.items.map(l => (
          <MenuItem key={l.id} value={l.id} sx={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <LayoutIcon layout={l} nativeGrids={config?.meta?.nativeGrids} />
            {l.id}
          </MenuItem>
        )),
      ])}
    </Select>
  )

  // ── Layout tabella: riga di testa + sub-righe collassabili ──────────────────
  if (isTable) {
    return (
      <div
        {...dragProps}
        style={{
          borderBottom: '1px solid #e5e7eb',
          background: isDragOver ? '#eff6ff' : (isSelected ? '#f5f5f5' : '#fff'),
          borderLeft: isSelected ? '3px solid #9e9e9e' : hasOverflow ? '3px solid #f59e0b' : '3px solid transparent',
          cursor: 'default',
          transition: 'background 0.1s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px' }}>
          <span {...handleProps} style={{ color: '#9ca3af', fontSize: 16, userSelect: 'none', flexShrink: 0, cursor: 'default' }}>⠿</span>
          <span
            onClick={e => { e.stopPropagation(); setSubRowsCollapsed(v => !v) }}
            title={subRowsCollapsed ? 'Expand rows' : 'Collapse rows'}
            style={{ fontSize: 10, color: '#9ca3af', cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
          >
            {subRowsCollapsed ? '▶' : '▼'}
          </span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#111', display: 'flex', alignItems: 'center', gap: 6 }}>
            {subRows.length} articles
            {totalChunks > 1 && (
              <span style={{ fontWeight: 400, fontSize: 11, color: '#9ca3af', background: '#f3f4f6', borderRadius: 4, padding: '1px 5px' }}>
                Part {chunkIndex + 1}/{totalChunks}
              </span>
            )}
          </span>
          {layoutSelect}
          <Select
            value={imgSize}
            onChange={e => onChange(index, 'imgSize', e.target.value)}
            size="small"
            onClick={e => e.stopPropagation()}
            sx={{ fontSize: 11, minWidth: 55, flexShrink: 0 }}
          >
            {['S', 'M', 'L'].map(s => (
              <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>
            ))}
          </Select>
        </div>

        {!subRowsCollapsed && (
          <div style={{ borderTop: '1px solid #f3f4f6' }}
            onMouseDown={e => e.stopPropagation()}>
            {subRows.map((row, rowIdx) => (
              <div
                key={rowIdx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  padding: '4px 8px',
                  borderBottom: rowIdx < subRows.length - 1 ? '1px solid #f3f4f6' : 'none',
                  background: '#fafafa',
                }}
              >
                {/* Spacer allineato a drag handle + overflow icon */}
                <span style={{ width: 38, flexShrink: 0 }} />
                <div style={{ flexShrink: 0, minWidth: 80, fontSize: 13, color: '#6b7280', paddingTop: 3 }}>
                  {row[F.articleNumber] || ''}
                </div>
                 <div style={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    value={brToNl(row[F.articleName])}
                    onChange={e => onChangeTableRow(index, rowIdx, F.articleName, nlToBr(e.target.value))}
                    placeholder="Nome articolo"
                    variant="standard"
                    size="small"
                    multiline
                    inputProps={{ style: { fontSize: 12, padding: '1px 0', color: '#111', lineHeight: 1.3, fontWeight: 700 } }}
                    sx={quietFieldSx}
                    fullWidth
                  />
                  <TextField
                    value={brToNl(row[F.articleDescription])}
                    onChange={e => onChangeTableRow(index, rowIdx, F.articleDescription, nlToBr(e.target.value))}
                    placeholder="Descrizione"
                    variant="standard"
                    size="small"
                    multiline
                    inputProps={{ style: { fontSize: 12, padding: '1px 0', color: '#111', lineHeight: 1.3 } }}
                    sx={quietFieldSx}
                    fullWidth
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── Layout bkg: header blocco + sub-righe stile standard ──────────────────
  if (isBkg) {
    return (
      <div
        {...dragProps}
        style={{
          borderBottom: '1px solid #e5e7eb',
          background: isDragOver ? '#eff6ff' : (isSelected ? '#f5f5f5' : '#fff'),
          borderLeft: isSelected ? '3px solid #9e9e9e' : '3px solid transparent',
          cursor: 'default',
          transition: 'background 0.1s',
        }}
      >
        {/* Header: drag + toggle + count + spacers allineati alle colonne + layout select */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px' }}>
          <span {...handleProps} style={{ color: '#9ca3af', fontSize: 16, userSelect: 'none', flexShrink: 0, cursor: 'default' }}>⠿</span>
          <span
            onClick={e => { e.stopPropagation(); setSubRowsCollapsed(v => !v) }}
            title={subRowsCollapsed ? 'Expand rows' : 'Collapse rows'}
            style={{ fontSize: 10, color: '#9ca3af', cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
          >
            {subRowsCollapsed ? '▶' : '▼'}
          </span>
          {/* Spacer overflow icon */}
          <span style={{ width: 16, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111', minWidth: 90, flexShrink: 0 }}>
            {subRows.length} articles
          </span>
          <span style={{ flex: 1 }} />
          {layoutSelect}
          {showSpaceFill && <span style={{ minWidth: ADVISOR_COL_W, width: ADVISOR_COL_W, flexShrink: 0 }} />}
          <span style={{ minWidth: 55, flexShrink: 0, marginLeft: 1 }} />
          {showImageFit && <span style={{ minWidth: ADVISOR_COL_W, width: ADVISOR_COL_W, flexShrink: 0 }} />}
        </div>

        {/* Sub-righe prodotto: stesse colonne delle righe standard */}
        {!subRowsCollapsed && (
          <div style={{ borderTop: '1px solid #f3f4f6' }}
            onMouseDown={e => e.stopPropagation()}>
            {subRows.map((row, rowIdx) => {
              const rowArtNum = row[F.articleNumber] || ''
              const rowArtName = row[F.articleName] || ''
              const rowArtText = row[F.articleDescription] || ''
              const slotKey = isBkg2 ? `${index}:${rowIdx}` : index
              const rowOverflow = overflowSet != null && overflowSet.has(slotKey)
              return (
                <div
                  key={rowIdx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 8px 4px 30px',
                    borderBottom: rowIdx < subRows.length - 1 ? '1px solid #f3f4f6' : 'none',
                    background: '#fafafa',
                  }}
                >
                  {rowOverflow ? (
                    <span title="Text overflow" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0, lineHeight: 1 }}>!</span>
                  ) : (
                    <span style={{ width: 16, flexShrink: 0 }} />
                  )}

                  <div style={{ flexShrink: 0, minWidth: 90 }}>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{rowArtNum}</div>
                  </div>

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <TextField
                      value={brToNl(rowArtName)}
                      onChange={e => onChangeTableRow(index, rowIdx, F.articleName, nlToBr(e.target.value))}
                      placeholder="Nome prodotto"
                      variant="standard"
                      size="small"
                      inputProps={{ style: { fontSize: 13, fontWeight: 700, color: '#111', padding: '1px 0' } }}
                      sx={quietFieldSx}
                      fullWidth
                    />
                    <TextField
                      value={brToNl(rowArtText)}
                      onChange={e => onChangeTableRow(index, rowIdx, F.articleDescription, nlToBr(e.target.value))}
                      placeholder="Descrizione"
                      variant="standard"
                      size="small"
                      multiline
                      inputProps={{ style: { fontSize: 12, padding: '1px 0', color: '#111', lineHeight: 1.3 } }}
                      sx={quietFieldSx}
                      fullWidth
                    />
                  </div>

                  {/* Spacer allineato a Layout column */}
                  <span style={{ minWidth: 90, flexShrink: 0 }} />

                  {showSpaceFill && <span style={{ minWidth: ADVISOR_COL_W, width: ADVISOR_COL_W, flexShrink: 0 }} />}

                  <Select
                    value={isBkg2 ? (row[F.imgSize] || imgSize || 'S') : (imgSize || 'S')}
                    onChange={e => {
                      e.stopPropagation()
                      if (isBkg2) {
                        onChangeTableRow(index, rowIdx, F.imgSize, e.target.value)
                      } else {
                        onChange(index, 'imgSize', e.target.value)
                      }
                    }}
                    size="small"
                    onClick={e => e.stopPropagation()}
                    sx={{ fontSize: 11, minWidth: 55, flexShrink: 0, marginLeft: 1 }}
                  >
                    {['S', 'M', 'L'].map(s => (
                      <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>
                    ))}
                  </Select>

                  {showImageFit && (() => {
                    const suggKey = isBkg2 ? `${index}:${rowIdx}` : index
                    const slotSuggestion = imageFitSuggestions?.get?.(suggKey) ?? imageFitSuggestion
                    return (
                    <AdvisorCell
                      advisor="imageFit"
                      suggestion={slotSuggestion}
                      undoEntry={undoEntries?.find(e => e.advisor === 'imageFit' && e.type === 'single' && e.index === suggKey)}
                      onAccept={() => onAcceptSuggestion?.('imageFit', suggKey)}
                      onReject={() => onRejectSuggestion?.('imageFit', suggKey)}
                      onUndo={onUndo}
                    />
                    )
                  })()}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // ── Layout img-only: riga compatta, solo drag handle + layout select ─────────
  if (currentGroup === 'img') {
    return (
      <div
        {...dragProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 8px',
          borderBottom: '1px solid #e5e7eb',
          background: isDragOver ? '#eff6ff' : (isSelected ? '#f5f5f5' : '#fff'),
          borderLeft: isSelected ? '3px solid #9e9e9e' : hasOverflow ? '3px solid #f59e0b' : '3px solid transparent',
          cursor: 'default',
          transition: 'background 0.1s',
        }}
      >
        <span {...handleProps} style={{ color: '#9ca3af', fontSize: 16, userSelect: 'none', flexShrink: 0, cursor: 'default' }}>⠿</span>
        {hasOverflow ? (
          <span title="Text overflow" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0, lineHeight: 1 }}>!</span>
        ) : (
          <span style={{ width: 16, flexShrink: 0 }} />
        )}
        <span style={{ minWidth: 90, flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {product?.[F.image] ? `Image: ${product[F.image]}` : ''}
        </span>
        {layoutSelect}
        {showSpaceFill && (
          <AdvisorCell
            advisor="spaceFill"
            suggestion={spaceFillSuggestion}
            undoEntry={undoEntries?.find(e => e.advisor === 'spaceFill' && e.type === 'single' && e.index === index)}
            onAccept={() => onAcceptSuggestion?.('spaceFill', index)}
            onReject={() => onRejectSuggestion?.('spaceFill', index)}
            onUndo={onUndo}
          />
        )}
        <span style={{ minWidth: 55, flexShrink: 0, marginLeft: 8 }} />
        {showImageFit && (
          <AdvisorCell
            advisor="imageFit"
            suggestion={imageFitSuggestion}
            undoEntry={undoEntries?.find(e => e.advisor === 'imageFit' && e.type === 'single' && e.index === index)}
            onAccept={() => onAcceptSuggestion?.('imageFit', index)}
            onReject={() => onRejectSuggestion?.('imageFit', index)}
            onUndo={onUndo}
          />
        )}
      </div>
    )
  }

  // ── Layout standard ──────────────────────────────────────────────────────────
  return (
    <div
      {...dragProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 8px',
        borderBottom: '1px solid #e5e7eb',
        background: isDragOver ? '#eff6ff' : (isSelected ? '#f5f5f5' : '#fff'),
        borderLeft: isSelected ? '3px solid #9e9e9e' : '3px solid transparent',
        cursor: 'default',
        transition: 'background 0.1s',
      }}
    >
      <span {...handleProps} style={{ color: '#9ca3af', fontSize: 16, userSelect: 'none', flexShrink: 0, cursor: 'default' }}>⠿</span>
      {hasOverflow ? (
        <span title="Text overflow" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0, lineHeight: 1 }}>!</span>
      ) : (
        <span style={{ width: 16, flexShrink: 0 }} />
      )}

      <div style={{ flexShrink: 0, minWidth: 90 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>{artNum}</div>
      </div>

      <div
        style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}
        onMouseDown={e => e.stopPropagation()}
      >
        <TextField
          value={brToNl(artName)}
          onChange={e => onChangeProduct(index, F.articleName, nlToBr(e.target.value))}
          placeholder="Nome prodotto"
          variant="standard"
          size="small"
          inputProps={{ style: { fontSize: 13, fontWeight: 700, color: '#111', padding: '1px 0' } }}
          sx={quietFieldSx}
          fullWidth
        />
        <TextField
          value={brToNl(artText)}
          onChange={e => onChangeProduct(index, F.articleDescription, nlToBr(e.target.value))}
          placeholder="Descrizione"
          variant="standard"
          size="small"
          multiline
          inputProps={{ style: { fontSize: 12, padding: '1px 0', color: '#111', lineHeight: 1.3 } }}
          sx={quietFieldSx}
          fullWidth
        />
      </div>

      {layoutSelect}
      {showSpaceFill && (
        <AdvisorCell
          advisor="spaceFill"
          active
          suggestion={spaceFillSuggestion}
          undoEntry={undoEntries?.find(e => e.advisor === 'spaceFill' && e.type === 'single' && e.index === index)}
          onAccept={() => onAcceptSuggestion?.('spaceFill', index)}
          onReject={() => onRejectSuggestion?.('spaceFill', index)}
          onUndo={onUndo}
        />
      )}

      <Select
        value={imgSize}
        onChange={e => onChange(index, 'imgSize', e.target.value)}
        size="small"
        sx={{ fontSize: 11, minWidth: 55, flexShrink: 0, marginLeft: 1 }}
      >
        {['S', 'M', 'L'].map(s => (
          <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>
        ))}
      </Select>
      {showImageFit && (
        <AdvisorCell
          advisor="imageFit"
          active
          suggestion={imageFitSuggestion}
          undoEntry={undoEntries?.find(e => e.advisor === 'imageFit' && e.type === 'single' && e.index === index)}
          onAccept={() => onAcceptSuggestion?.('imageFit', index)}
          onReject={() => onRejectSuggestion?.('imageFit', index)}
          onUndo={onUndo}
        />
      )}
    </div>
  )
}

// ─── PageHeader ───────────────────────────────────────────────────────────────

function PageHeader({ pageNum, isActive, isCollapsed, onClick, onToggle, activeAdvisors, imageFitPending, spaceFillPending, onApplyAllPage, undoEntries, onUndo, onDismissUndo, showSpaceFill, showImageFit }) {
  const ifUndo = undoEntries.find(e => e.advisor === 'imageFit' && e.type === 'page' && e.pageNum === pageNum)
  const sfUndo = undoEntries.find(e => e.advisor === 'spaceFill' && e.type === 'page' && e.pageNum === pageNum)

  function advisorSlot(advisor, pending, undo) {
    const colors = MODE_COLORS[advisor]
    if (undo) return (
      <span style={{ minWidth: ADVISOR_COL_W, width: ADVISOR_COL_W, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <UndoButton undoId={undo.id} onUndo={onUndo} size="small" />
      </span>
    )
    const canApply = activeAdvisors.has(advisor) && pending > 0
    return (
      <span
        onClick={canApply ? (e => { e.stopPropagation(); onApplyAllPage?.(advisor, pageNum) }) : undefined}
        style={{
          minWidth: ADVISOR_COL_W, width: ADVISOR_COL_W, flexShrink: 0,
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
          padding: '3px 0', borderRadius: 3,
          background: canApply ? colors.main : 'transparent',
          color: canApply ? '#fff' : 'transparent',
          cursor: canApply ? 'pointer' : 'default',
        }}
      >
        {canApply ? `Apply all (${pending})` : ''}
      </span>
    )
  }

  return (
    <div
      data-list-page-num={pageNum}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 10px',
        minHeight: 34,
        background: isActive ? '#e8e8e8' : '#f0f0f0',
        borderBottom: '1px solid #e2e8f0',
        borderLeft: isActive ? '3px solid #9e9e9e' : '3px solid transparent',
        fontSize: 11,
        fontWeight: 600,
        color: '#475569',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        transition: 'background 0.2s',
        cursor: 'pointer',
        userSelect: 'none',
        position: 'sticky',
        top: 22,
        zIndex: 4,
      }}
    >
      <span
        onClick={e => { e.stopPropagation(); onToggle?.() }}
        style={{ fontSize: 9, opacity: 0.7, padding: '0 2px', cursor: 'pointer' }}
      >
        {isCollapsed ? '▶' : '▼'}
      </span>
      Page {pageNum}
      <span style={{ flex: 1 }} />
      <span style={{ minWidth: 90, flexShrink: 0 }} />
      {showSpaceFill && advisorSlot('spaceFill', spaceFillPending, sfUndo)}
      <span style={{ minWidth: 55, flexShrink: 0, marginLeft: 1 }} />
      {showImageFit && advisorSlot('imageFit', imageFitPending, ifUndo)}
    </div>
  )
}

// ─── PlacementList ────────────────────────────────────────────────────────────

export function PlacementList({
  placements,
  config,
  onReorder,
  onChange,
  onChangeProduct,
  onChangeTableRow,
  onHover,
  pageStartMap = {},
  activePage = 1,
  onPageClick,
  scrollContainerRef,
  selectedIndex = null,
  onRowClick = null,
  overflowSet = null,
  activeAdvisors = new Set(),
  imageFitSuggestions = new Map(),
  spaceFillSuggestions = new Map(),
  onAcceptSuggestion = null,
  onRejectSuggestion = null,
  onApplyAllPage = null,
  undoEntries = [],
  onUndo = null,
  onDismissUndo = null,
  hasCoverProducts = false,
  coverAllowedLayouts = ['S', 'S2', 'M', 'L'],
  indexToPage = {},
}) {
  const [dragIdx, setDragIdx] = useState(null)
  const [overIdx, setOverIdx] = useState(null)
  const [collapsedPages, setCollapsedPages] = useState(new Set())
  const rowRefs = useRef({})

  const showSpaceFill = activeAdvisors.has('spaceFill')
  const showImageFit = activeAdvisors.has('imageFit')

  // Mappa: indice placement → numero pagina
  const rowToPage = {}
  let curPage = 1
  for (let i = 0; i < (placements?.length ?? 0); i++) {
    if (pageStartMap[i] != null) curPage = pageStartMap[i]
    rowToPage[i] = curPage
  }

  // Auto-espandi la pagina e scrolla alla riga quando selectedIndex cambia
  useEffect(() => {
    if (selectedIndex === null) return
    const pageNum = rowToPage[selectedIndex]
    if (pageNum != null) {
      setCollapsedPages(prev => {
        if (!prev.has(pageNum)) return prev
        const next = new Set(prev)
        next.delete(pageNum)
        return next
      })
    }
    // Scroll dopo il render (dopo eventuale espansione)
    const t = setTimeout(() => {
      rowRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 50)
    return () => clearTimeout(t)
  }, [selectedIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  function togglePage(pageNum) {
    setCollapsedPages(prev => {
      const next = new Set(prev)
      if (next.has(pageNum)) next.delete(pageNum)
      else next.add(pageNum)
      return next
    })
  }

  function handleDragStart(i) { setDragIdx(i) }
  function handleDragOver(i) { if (overIdx !== i) setOverIdx(i) }

  // Auto-scroll durante il drag: quando il cursore è vicino al bordo superiore/inferiore
  // del container scrollabile, scrolla automaticamente nella direzione corrispondente.
  const autoScrollRef = useRef(null)
  function handleContainerDragOver(e) {
    const container = scrollContainerRef?.current
    if (!container) return
    const rect = container.getBoundingClientRect()
    const EDGE = 40  // px dalla soglia bordo
    const SPEED = 8  // px per frame
    const y = e.clientY

    // Cancella eventuale scroll precedente
    if (autoScrollRef.current) { cancelAnimationFrame(autoScrollRef.current); autoScrollRef.current = null }

    if (y - rect.top < EDGE) {
      // Scroll verso l'alto
      ; (function scrollUp() {
        container.scrollTop -= SPEED
        if (container.scrollTop > 0) autoScrollRef.current = requestAnimationFrame(scrollUp)
      })()
    } else if (rect.bottom - y < EDGE) {
      // Scroll verso il basso
      ; (function scrollDown() {
        container.scrollTop += SPEED
        const maxScroll = container.scrollHeight - container.clientHeight
        if (container.scrollTop < maxScroll) autoScrollRef.current = requestAnimationFrame(scrollDown)
      })()
    }
  }

  function handleContainerDragLeave() {
    if (autoScrollRef.current) { cancelAnimationFrame(autoScrollRef.current); autoScrollRef.current = null }
  }

  function handleDrop() {
    if (dragIdx !== null && overIdx !== null && dragIdx !== overIdx) {
      const next = [...placements]
      const [item] = next.splice(dragIdx, 1)
      next.splice(overIdx, 0, item)
      onReorder(next)
    }
    setDragIdx(null)
    setOverIdx(null)
  }

  // Resetta sempre lo stato drag — scatta anche se il drop avviene fuori target
  function handleDragEnd() {
    if (autoScrollRef.current) { cancelAnimationFrame(autoScrollRef.current); autoScrollRef.current = null }
    setDragIdx(null)
    setOverIdx(null)
  }

  // Conta suggerimenti pendenti per pagina per ciascun advisor
  function placementIdx(key) {
    if (typeof key === 'string' && key.includes(':')) return Number(key.split(':')[0])
    return key
  }
  const ifPendingByPage = {}
  const sfPendingByPage = {}
  for (const [idx, s] of imageFitSuggestions) {
    if (s.status === 'pending') {
      const pg = rowToPage[placementIdx(idx)]
      if (pg) ifPendingByPage[pg] = (ifPendingByPage[pg] || 0) + 1
    }
  }
  for (const [idx, s] of spaceFillSuggestions) {
    if (s.status === 'pending') {
      const pg = rowToPage[placementIdx(idx)]
      if (pg) sfPendingByPage[pg] = (sfPendingByPage[pg] || 0) + 1
    }
  }

  return (
    <div
      ref={scrollContainerRef}
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
      onDrop={handleContainerDragLeave}
      style={{ height: '100%', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff' }}
    >
      {/* Header colonne: allineato alle colonne delle righe */}
      <div data-list-column-header style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        position: 'sticky',
        top: 0,
        zIndex: 5,
      }}>
        {/* Spacer per drag handle + overflow icon */}
        <span style={{ minWidth: 20, flexShrink: 0 }} />
        <span style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 90, flexShrink: 0 }}>Article</span>
        <span style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>Description</span>
        <span style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 90, textAlign: 'center', flexShrink: 0 }}>Layout</span>
        {showSpaceFill && (
          <span style={{ fontSize: 9, fontWeight: 700, color: MODE_COLORS.spaceFill.main, textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: ADVISOR_COL_W, width: ADVISOR_COL_W, textAlign: 'center', flexShrink: 0 }}>Space Fill</span>
        )}
        <span style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 55, textAlign: 'center', flexShrink: 0, marginLeft: 8 }}>Img Size</span>
        {showImageFit && (
          <span style={{ fontSize: 9, fontWeight: 700, color: MODE_COLORS.imageFit.main, textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: ADVISOR_COL_W, width: ADVISOR_COL_W, textAlign: 'center', flexShrink: 0 }}>Image Fit</span>
        )}
      </div>
      {placements.flatMap((p, i) => {
        const items = []
        const pageNum = pageStartMap[i]

        if (pageNum != null) {
          const isCollapsed = collapsedPages.has(pageNum)
          items.push(<div key={`anchor-${i}`} data-list-page-anchor={pageNum} />)
          items.push(
            <PageHeader
              key={`page-${i}`}
              pageNum={pageNum}
              isActive={pageNum === activePage}
              isCollapsed={isCollapsed}
              onClick={() => { onRowClick?.(null); onPageClick?.(pageNum) }}
              onToggle={() => togglePage(pageNum)}
              activeAdvisors={activeAdvisors}
              imageFitPending={ifPendingByPage[pageNum] || 0}
              spaceFillPending={sfPendingByPage[pageNum] || 0}
              onApplyAllPage={onApplyAllPage}
              undoEntries={undoEntries}
              onUndo={onUndo}
              onDismissUndo={onDismissUndo}
              showSpaceFill={showSpaceFill}
              showImageFit={showImageFit}
            />
          )
        }

        // Salta le righe se la pagina è collassata
        if (!collapsedPages.has(rowToPage[i])) {
          items.push(
            <div key={`row-wrap-${i}`} ref={el => { rowRefs.current[i] = el }}>
              <PlacementRow
                index={i}
                placement={p}
                config={config}
                onChange={onChange}
                onChangeProduct={onChangeProduct}
                onChangeTableRow={onChangeTableRow}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                isDragOver={overIdx === i}
                onHover={onHover}
                isSelected={selectedIndex === i}
                onRowClick={onRowClick}
                hasOverflow={overflowSet != null && overflowSet.has(i)}
                overflowSet={overflowSet}
                activeAdvisors={activeAdvisors}
                imageFitSuggestion={imageFitSuggestions.get(i)}
                imageFitSuggestions={imageFitSuggestions}
                spaceFillSuggestion={spaceFillSuggestions.get(i)}
                onAcceptSuggestion={onAcceptSuggestion}
                onRejectSuggestion={onRejectSuggestion}
                undoEntries={undoEntries}
                onUndo={onUndo}
                showSpaceFill={showSpaceFill}
                showImageFit={showImageFit}
                allowedLayouts={hasCoverProducts && indexToPage[i] === 1 ? coverAllowedLayouts : null}
              />
            </div>
          )
        }

        return items
      })}
    </div>
  )
}
